/**
 * The "contributor" placeholder role.
 *
 * "Contributor" is the taxonomy's catch-all: it records that someone took part
 * in a recording without saying what they did. It only carries information on
 * a recording that has nothing more specific, so every surface that edits a
 * single recording's contributions treats it as mutually exclusive with the
 * other roles:
 *
 *  - A recording with no contributions may take "contributor", but only at a
 *    red (Unverified) or gray (Unknown) data-quality category — it is by
 *    definition not a verified credit.
 *  - A recording that already has a role never offers "contributor".
 *  - Adding a specific role to a recording whose only role is "contributor"
 *    drops "contributor" from the list automatically.
 *
 * Kept as pure helpers so the recording edit form and the mass-edit modal
 * apply the same rule, and so it can be tested without a DOM.
 */

export const CONTRIBUTOR_ROLE = 'contributor';

/** Data-quality categories the placeholder may carry: 4 (red) and 5 (gray).
 *  See `CONFIDENCE_CATEGORIES` in `ContributionDetailModal`. */
export const CONTRIBUTOR_CONFIDENCE_CATEGORIES: readonly string[] = ['4', '5'];

export const isContributorRole = (role?: string | null): boolean =>
  (role ?? '').trim().toLowerCase() === CONTRIBUTOR_ROLE;

/** The categories a role may be saved at, or `undefined` when unrestricted. */
export const confidenceCategoriesFor = (
  role?: string | null
): readonly string[] | undefined =>
  isContributorRole(role) ? CONTRIBUTOR_CONFIDENCE_CATEGORIES : undefined;

/** Coerce a category into the role's allowed set. A category that is not
 *  allowed snaps to the first allowed one, so a "contributor" seeded with the
 *  default `'1'` opens on red rather than on a choice it cannot keep. */
export const clampConfidenceCategory = (
  role: string | null | undefined,
  category: string
): string => {
  const allowed = confidenceCategoriesFor(role);
  if (!allowed || allowed.includes(category)) return category;
  return allowed[0];
};

type HasRole = { contribution?: string };

/**
 * The roles the add dropdown may offer, given what the recording already has.
 * Roles already assigned are never offered; "contributor" is offered only to a
 * recording with no contributions at all.
 */
export function offerableContributions<T extends HasRole>(
  options: readonly string[],
  existing: readonly T[]
): string[] {
  const taken = new Set(
    existing.map((c) => (c.contribution ?? '').toLowerCase())
  );
  return options.filter(
    (role) =>
      !taken.has(role.toLowerCase()) &&
      !(existing.length > 0 && isContributorRole(role))
  );
}

/**
 * Append a contribution to a recording's list. Adding any specific role
 * evicts the "contributor" placeholder, since the recording now says what the
 * contribution was.
 */
export function withContributionAdded<T extends HasRole>(
  existing: readonly T[],
  added: T
): T[] {
  const kept = isContributorRole(added.contribution)
    ? existing
    : existing.filter((c) => !isContributorRole(c.contribution));
  return [...kept, added];
}
